import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import "./ImageDialog.css";

const DEFAULT_ASPECT_RATIO = 1;
const CANVAS_SIZE = 512;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AvatarImageDialog = ({ isOpen, initialFile, onClose, onComplete }) => {
  const cropperRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setImageSrc(null);
      setCurrentFile(null);
      setIsProcessing(false);
      return;
    }

    if (initialFile) {
      setCurrentFile(initialFile);
      fileToDataUrl(initialFile)
        .then(setImageSrc)
        .catch(() => setImageSrc(null));
    } else {
      setImageSrc(null);
      setCurrentFile(null);
    }
  }, [isOpen, initialFile]);

  useEffect(() => {
    if (imageSrc && cropperRef.current) {
      cropperRef.current.cropper.setAspectRatio(DEFAULT_ASPECT_RATIO);
    }
  }, [imageSrc]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return;
    }
    setCurrentFile(file);
    fileToDataUrl(file)
      .then(setImageSrc)
      .catch(() => setImageSrc(null));
  };

  const triggerFileInput = () => {
    if (typeof document === "undefined") return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = handleFileSelect;
    input.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return;
    }
    setCurrentFile(file);
    fileToDataUrl(file)
      .then(setImageSrc)
      .catch(() => setImageSrc(null));
  };

  const handleDragOver = (event) => event.preventDefault();

  const handleCancel = () => {
    if (isProcessing) return;
    onClose();
  };

  const resolveFileName = (originalFile) => {
    if (originalFile && originalFile.name) {
      const base = originalFile.name.replace(/\.[^.]+$/, "");
      return `${base}-avatar-${Date.now()}.png`;
    }
    return `avatar-${Date.now()}.png`;
  };

  const handleApply = async () => {
    if (!imageSrc) {
      triggerFileInput();
      return;
    }

    if (!cropperRef.current) {
      return;
    }

    const cropper = cropperRef.current.cropper;
    setIsProcessing(true);

    try {
      const canvas = cropper.getCroppedCanvas({
        width: CANVAS_SIZE,
        height: CANVAS_SIZE,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });

      const dataUrl = canvas.toDataURL("image/png", 0.92);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error("無法轉換裁切結果"));
          }
        }, "image/png", 0.92);
      });

      const fileName = resolveFileName(currentFile || initialFile);
      const file = new File([blob], fileName, { type: "image/png" });

      const wasSuccessful = await onComplete({
        file,
        dataUrl,
        aspectRatio: DEFAULT_ASPECT_RATIO,
      });

      setIsProcessing(false);

      if (wasSuccessful) {
        onClose();
      }
    } catch (error) {
      console.error("頭像裁切失敗", error);
      alert("裁切頭像時發生錯誤，請重試");
      setIsProcessing(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const dialog = (
    <div
      className="image-dialog-overlay"
      onClick={(event) => event.target === event.currentTarget && handleCancel()}
    >
      <div className="image-dialog cover-image-dialog avatar-image-dialog">
        <div className="image-dialog-header">
          <h3>調整頭像</h3>
          <button className="image-dialog-close" onClick={handleCancel} aria-label="關閉頭像裁切器">
            ×
          </button>
        </div>

        <div className="image-dialog-content">
          {!imageSrc ? (
            <div
              className="image-upload-area"
              onClick={triggerFileInput}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="upload-icon">🖼️</div>
              <div className="upload-text">選擇或拖放新的頭像</div>
              <div className="upload-hint">建議尺寸 512×512，支援 JPG、PNG、WebP</div>
            </div>
          ) : (
            <div className="image-cropper-container">
              <div className="cropper-wrapper">
                <Cropper
                  src={imageSrc}
                  style={{ height: "100%", width: "100%" }}
                  initialAspectRatio={DEFAULT_ASPECT_RATIO}
                  guides
                  viewMode={1}
                  background={false}
                  responsive
                  autoCropArea={0.95}
                  checkOrientation={false}
                  ref={cropperRef}
                />
              </div>
              <div className="cropper-fixed-hint">頭像比例已固定為 1:1</div>
            </div>
          )}
        </div>

        <div className="image-dialog-actions">
          <button
            type="button"
            className="image-btn secondary"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            取消
          </button>
          <button
            type="button"
            className="image-btn primary"
            onClick={handleApply}
            disabled={isProcessing || !imageSrc}
          >
            {isProcessing ? "處理中..." : "套用頭像"}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return dialog;
  }

  return createPortal(dialog, document.body);
};

export default AvatarImageDialog;
